import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Text } from '../../design/text';
import LoadingView from '../../components/LoadingView';

interface AuctionHouseMetrics {
  id: string;
  name: string;
  slug: string;
  totalLots: number;
  soldLots: number;
  unsoldLots: number;
  sellThroughRate: number;
  totalHammerValue: number;
  avgHammerPrice: number;
  performanceVsEstimate: number;
  marketStrength: string;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toLocaleString()}`;
};

const getStrengthStyles = (strength: string): string => {
  switch (strength.toLowerCase()) {
    case 'exceptional':
      return 'bg-emerald-50 text-emerald-800 border border-emerald-200';
    case 'strong':
      return 'bg-blue-50 text-blue-800 border border-blue-200';
    case 'moderate':
      return 'bg-amber-50 text-amber-800 border border-amber-200';
    case 'fair':
      return 'bg-rose-50 text-rose-800 border border-rose-200';
    default:
      return 'bg-gallery-100 text-gallery-600 border border-gallery-200';
  }
};

const AuctionHousesIndex: React.FC = () => {
  const [auctionHouses, setAuctionHouses] = useState<AuctionHouseMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuctionHouses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/auction-houses`);
        const data = response.data?.data || response.data;
        setAuctionHouses(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Failed to load auction houses');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionHouses();
  }, []);

  if (loading) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Text className="text-rose-600">{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="font-display text-display-sm text-gallery-900 mb-3">
            Auction House Performance
          </h1>
          <p className="text-gallery-500 max-w-2xl">
            Comprehensive analysis of leading auction houses. Select any institution
            to explore detailed year-over-year performance metrics.
          </p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {auctionHouses.map((house, index) => (
            <Link
              key={house.id}
              to={`/auction-houses/${house.slug}`}
              className="group bg-white border border-gallery-200 rounded-sm overflow-hidden transition-all duration-300 hover:border-gallery-400 hover:shadow-gallery-lg animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Card Header */}
              <div className="px-6 pt-6 pb-4 border-b border-gallery-100">
                <h3 className="font-display text-xl text-gallery-900 group-hover:text-gallery-700 transition-colors">
                  {house.name}
                </h3>
              </div>

              {/* Metrics Grid */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="stat-value">
                      {house.totalLots.toLocaleString()}
                    </div>
                    <div className="stat-label mt-1">Total Lots</div>
                  </div>

                  <div>
                    <div className="stat-value">
                      {house.sellThroughRate}%
                    </div>
                    <div className="stat-label mt-1">Sell-Through</div>
                  </div>

                  <div>
                    <div className="stat-value">
                      {formatCurrency(house.totalHammerValue)}
                    </div>
                    <div className="stat-label mt-1">Total Sales</div>
                  </div>

                  <div>
                    <div className={`stat-value ${house.performanceVsEstimate >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {house.performanceVsEstimate > 0 ? '+' : ''}
                      {house.performanceVsEstimate}%
                    </div>
                    <div className="stat-label mt-1">vs Estimate</div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gallery-100">
                  <span className={`inline-block px-3 py-1.5 rounded-sm text-xs font-medium tracking-wide ${getStrengthStyles(house.marketStrength)}`}>
                    {house.marketStrength}
                  </span>
                  <span className="text-sm text-gallery-400 group-hover:text-gallery-600 transition-colors flex items-center gap-1">
                    View Details
                    <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {auctionHouses.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gallery-100 mb-4">
              <svg className="w-8 h-8 text-gallery-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="font-display text-lg text-gallery-700 mb-2">No Auction Houses Found</h3>
            <p className="text-gallery-500">Check back later for performance data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionHousesIndex;
